import { getCookie } from './cookies';
import { refreshTokenApi } from './authApi';

/**
 * Wrapper around fetch that automatically retries once on 401 by invoking refreshToken.
 * If refresh fails, returns original 401.
 */
export async function fetchWithAuth(url, options = {}) {
  const { timeout = 10000, ...restOptions } = options;
  const controller = new AbortController();
  const timer = setTimeout(() => {
    console.log(`[FETCH_AUTH] Request timeout after ${timeout}ms, aborting...`);
    controller.abort();
  }, timeout);

  console.log(`[FETCH_AUTH] Making authenticated request to: ${url}`);
  console.log(`[FETCH_AUTH] Using timeout: ${timeout}ms`);
  
  // Get CSRF token from cookie
  const csrfToken = getCookie('csrf_token');
  console.log(`[FETCH_AUTH] CSRF token:`, csrfToken ? `${csrfToken.substring(0, 10)}...` : 'None');
  
  // Prepare headers with CSRF token
  const headers = {
    // Only set Content-Type if not explicitly overridden (for FormData uploads)
    ...(restOptions.headers && !restOptions.headers.hasOwnProperty('Content-Type') ? { 'Content-Type': 'application/json' } : {}),
    ...restOptions.headers,
    ...(csrfToken && { 'X-CSRF-Token': csrfToken })
  };
  
  // Remove Content-Type if it's explicitly set to undefined/null (for FormData)
  if (restOptions.headers && restOptions.headers['Content-Type'] === undefined) {
    delete headers['Content-Type'];
  }
  
  console.log(`[FETCH_AUTH] Request headers:`, Object.keys(headers));

  // Make the request with credentials
  const requestOptions = {
    ...restOptions,
    headers,
    credentials: 'include', // Include cookies
    signal: controller.signal
  };

  try {
    console.log(`[FETCH_AUTH] Sending request with credentials: include`);
    const response = await fetch(url, requestOptions);
    
    console.log(`[FETCH_AUTH] Response status: ${response.status} (${response.statusText})`);
    
    // If unauthorized, try to refresh token once
    if (response.status === 401) {
      console.log('[FETCH_AUTH] Received 401, attempting token refresh...');
      const refreshSuccess = await refreshTokenApi();
      
      console.log('[FETCH_AUTH] Token refresh result:', refreshSuccess ? 'Success' : 'Failed');
      
      if (refreshSuccess) {
        // Retry the original request with new token
        const newCsrfToken = getCookie('csrf_token');
        console.log('[FETCH_AUTH] Retrying with new CSRF token:', newCsrfToken ? `${newCsrfToken.substring(0, 10)}...` : 'None');
        
        const retryHeaders = {
          ...headers,
          ...(newCsrfToken && { 'X-CSRF-Token': newCsrfToken })
        };
        
        const retryResponse = await fetch(url, {
          ...requestOptions,
          headers: retryHeaders
        });
        
        console.log(`[FETCH_AUTH] Retry response status: ${retryResponse.status}`);
        return retryResponse;
      } else {
        console.log('[FETCH_AUTH] Token refresh failed, returning original 401 response');
      }
    }
    
    return response;
  } catch (error) {
    console.error('[FETCH_AUTH] Fetch error:', error);
    if (window.__showNotification) {
      window.__showNotification('Network error. Please check your connection and try again.', 'error');
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}
